'use client';

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useProjectStore } from '@/stores/project-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { getPickerIcon } from '@/lib/picker-icons';
import type { Ticket as TicketType } from 'dico-types';

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface TicketCardRowProps {
  ticket: TicketType;
  workspaceId: string;
  /** When true, render as non-navigating div (e.g. inside a draggable that handles click) */
  asDiv?: boolean;
  className?: string;
}

function TicketMeta({ ticket, workspaceId }: { ticket: TicketType; workspaceId: string }) {
  const getProjects = useProjectStore((s) => s.getProjects);
  const projects = getProjects(workspaceId);
  const project = ticket.projectId ? projects.find((p) => p.id === ticket.projectId) : null;
  const projectIconType = (project?.icon ? getPickerIcon(project.icon) : null) ?? 'Layers';

  return (
    <div className="text-muted-foreground mt-2 flex items-center gap-3 text-xs">
      {project && (
        <Badge variant="outline" className="h-4 gap-1 px-1.5 text-[10px]">
          <span style={{ color: project.color || undefined }}>
            <MonoIcon type={projectIconType} />
          </span>
          {project.name}
        </Badge>
      )}
      <span className="flex items-center gap-1">
        <MonoIcon type="Clock" />
        {formatRelativeTime(ticket.createdAt)}
      </span>
      {ticket.conversationIds.length > 0 && (
        <span className="flex items-center gap-1">
          {ticket.conversationIds.length} event{ticket.conversationIds.length !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

const rowBaseClass =
  'flex w-full items-start gap-3 rounded-sm p-1 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export function TicketCardRow({ ticket, workspaceId, asDiv, className = '' }: TicketCardRowProps) {
  const published = Boolean(ticket.responses?.some((r) => r.status === 'published'));
  const href = `/workspace/${workspaceId}/tickets/${ticket.id}`;

  const getStatuses = useWorkspaceStore((s) => s.getStatuses);
  const getPriorities = useWorkspaceStore((s) => s.getPriorities);
  const getTags = useWorkspaceStore((s) => s.getTags);

  const statuses = getStatuses(workspaceId);
  const priorities = getPriorities(workspaceId);
  const tags = getTags(workspaceId);

  const status = ticket.statusId ? statuses.find((s) => s.id === ticket.statusId) : null;
  const priority = ticket.priorityId ? priorities.find((p) => p.id === ticket.priorityId) : null;

  const selectedTags = tags.filter((t) => ticket.tagIds?.includes(t.id));
  const priorityIconType = priority?.icon ? getPickerIcon(priority.icon) : null;
  const statusIconType = status?.icon ? getPickerIcon(status.icon) : null;
  const content = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
        {ticket.priorityId && priority && (
          <span
            className="ml-2 shrink-0 basis-4 text-sm"
            style={{ color: priority.color || undefined }}
          >
            <MonoIcon type={priorityIconType ?? 'Circle'} />
          </span>
        )}

        {ticket.issueKey && (
          <span className="text-muted-foreground shrink-0 basis-14 truncate text-xs">
            {ticket.issueKey}
          </span>
        )}

        <span className="text-foreground max-w-90 min-w-0 truncate text-sm font-medium">
          {ticket.title}
        </span>

        <div className="ml-auto flex items-center gap-4">
          {selectedTags.length > 0 && (
            <div className="flex shrink-0 gap-1">
              {selectedTags.map((tag) => (
                <Badge key={tag.id} variant="outline">
                  {tag.name}
                </Badge>
              ))}
            </div>
          )}
          <div className="text-muted-foreground shrink-0 basis-14 text-xs">
            {formatRelativeTime(ticket.createdAt)}
          </div>
        </div>
      </div>
    </>
  );

  if (asDiv) {
    return <div className={cn(rowBaseClass, className)}>{content}</div>;
  }

  return (
    <Link to={href} className={cn(rowBaseClass, className)}>
      {content}
    </Link>
  );
}

interface TicketCardCardProps {
  ticket: TicketType;
  workspaceId: string;
  /** When true, render as non-navigating div (e.g. inside a draggable that handles click) */
  asDiv?: boolean;
  className?: string;
}

const cardBaseClass =
  'flex w-full flex-col gap-2 rounded-md bg-card shadow-xs border border-border p-3 text-left transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export function TicketCardCard({
  ticket,
  workspaceId,
  asDiv,
  className = ''
}: TicketCardCardProps) {
  const href = `/workspace/${workspaceId}/tickets/${ticket.id}`;

  const getPriorities = useWorkspaceStore((s) => s.getPriorities);
  const getTags = useWorkspaceStore((s) => s.getTags);
  const priorities = getPriorities(workspaceId);
  const tags = getTags(workspaceId);

  const priority = ticket.priorityId ? priorities.find((p) => p.id === ticket.priorityId) : null;
  const selectedTags = tags.filter((t) => ticket.tagIds?.includes(t.id));
  const priorityIconType = priority?.icon ? getPickerIcon(priority.icon) : null;

  const titleAndMeta = (
    <>
      {ticket.issueKey && <div className="text-muted-foreground text-xs">{ticket.issueKey}</div>}
      <div className="min-w-0 flex-1">
        <p className="text-foreground line-clamp-2 text-sm font-medium">{ticket.title}</p>
      </div>
    </>
  );

  const showMetaRow = Boolean(priority) || selectedTags.length > 0;

  const metaRow = showMetaRow && (
    <div className="flex flex-col gap-1">
      {priority && (
        <Badge variant="outline">
          <MonoIcon
            type={priorityIconType ?? 'Circle'}
            style={priority.color ? { color: priority.color } : undefined}
          />
          {priority.name}
        </Badge>
      )}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {selectedTags.map((tag) => {
            const tagIconType = tag.icon ? getPickerIcon(tag.icon) : null;
            return (
              <Badge key={tag.id} variant="outline">
                {tagIconType && (
                  <MonoIcon
                    type={tagIconType}
                    style={tag.color ? { color: tag.color } : undefined}
                  />
                )}
                {tag.name}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );

  if (asDiv) {
    return (
      <div className={cn(cardBaseClass, className)}>
        {titleAndMeta}
        <div className="-m-1 mt-0">{metaRow}</div>
      </div>
    );
  }

  return (
    <div className={cn(cardBaseClass, className)}>
      <Link
        to={href}
        className="focus-visible:ring-ring flex min-w-0 flex-col gap-1 rounded-sm outline-offset-2 focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {titleAndMeta}
      </Link>
      <div className="-m-1 mt-0">{metaRow}</div>
    </div>
  );
}
